import clsx from 'clsx';

import { Button as MuiButton, buttonClasses as muiButtonClasses, css, styled } from '@mui/material';

import { Spinner } from '../Spinner';
import { ButtonBaseSpinnerSizeMap } from './constants';
import { buttonBaseClasses } from './theme';

import type { ButtonBaseOwnerState, ButtonBaseProps } from './types';

export const ButtonBase = (props: ButtonBaseProps) => {
  const {
    appearance,
    children,
    className,
    disabled = false,
    loading = false,
    loadingPosition = 'center',
    endIcon,
    onClick,
    ref,
    size = 'xl',
    startIcon,
    variant = 'accent',
    ...others
  } = props;

  const ownerState: ButtonBaseOwnerState = {
    ...props,
    appearance,
    disabled,
    loading,
    loadingPosition,
    size,
    variant,
  };

  const commonProps = {
    className: clsx(className),
    disabled,
    disableElevation: true,
    disableRipple: true,
    onClick,
    ownerState,
    ref,
    size,
    ...others,
  };

  return (
    <ButtonBaseRoot
      {...commonProps}
      endIcon={endIcon}
      loading={loading || undefined}
      loadingIndicator={loading ? <Spinner size={ButtonBaseSpinnerSizeMap[size]} /> : undefined}
      loadingPosition={loadingPosition}
      startIcon={startIcon}
      variant={appearance === 'solid' ? 'contained' : 'text'}
    >
      {children}
    </ButtonBaseRoot>
  );
};

ButtonBase.displayName = 'ButtonBase';

// 스타일 헬퍼 함수들
const getAppearanceVariantStyles = (
  appearance: ButtonBaseProps['appearance'],
  variant: ButtonBaseProps['variant'],
  theme: any,
) => {
  if (appearance === 'solid') {
    switch (variant) {
      case 'accent':
        return css`
          background-color: ${theme.color['actionSurface/accent/default']};
          color: ${theme.color['text/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/accent/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/accent/press']};
          }
          &:disabled {
            color: ${theme.color['text/disabled']};
            background-color: ${theme.color['actionSurface/disabled']};
          }
        `;
      case 'primary':
        return css`
          background-color: ${theme.color['actionSurface/primary/default']};
          color: ${theme.color['text/inverse/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/primary/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/primary/press']};
          }
          &:disabled {
            color: ${theme.color['text/disabled']};
            background-color: ${theme.color['actionSurface/disabled']};
          }
        `;
      case 'secondary':
        return css`
          background-color: ${theme.color['actionSurface/secondary/default']};
          color: ${theme.color['text/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/secondary/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/secondary/press']};
          }
          &:disabled {
            color: ${theme.color['text/disabled']};
            background-color: ${theme.color['actionSurface/disabled']};
          }
        `;
      case 'tertiary':
        return css`
          background-color: ${theme.color['actionSurface/tertiary/default']};
          color: ${theme.color['text/default']};
          border: 1px solid ${theme.color['actionBorder/tertiary/default']};
          &:hover {
            border: 1px solid ${theme.color['actionBorder/tertiary/hover']};
          }
          &:active {
            border: 1px solid ${theme.color['actionBorder/tertiary/press']};
          }
          &:disabled {
            background-color: ${theme.color['actionSurface/tertiary/default']};
            color: ${theme.color['text/disabled']};
            border: 1px solid ${theme.color['actionBorder/tertiary/disabled']};
          }
          ${theme.mixins.focusRing('outside', { offset: -3 })}
        `;
      case 'danger':
        return css`
          background-color: ${theme.color['actionSurface/danger/default']};
          color: ${theme.color['text/inverse/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/danger/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/danger/press']};
          }
          &:disabled {
            color: ${theme.color['text/inverse/default']};
            background-color: ${theme.color['actionSurface/danger/disabled']};
          }
        `;
      case 'inverse':
        return css`
          background-color: ${theme.color['actionSurface/tertiaryInverse/default']};
          color: ${theme.color['text/global/default']};
          ${theme.mixins.focusRing('outside', { inverse: true })}
          &:hover {
            background-color: ${theme.color['actionSurface/tertiaryInverse/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/tertiaryInverse/press']};
          }
          &:disabled {
            background-color: ${theme.color['actionSurface/tertiaryInverse/disabled']};
            color: ${theme.color['text/inverse/disabled']};
          }
        `;
    }
  } else if (appearance === 'ghost') {
    switch (variant) {
      case 'primary':
        return css`
          color: ${theme.color['text/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/ghost/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/ghost/press']};
          }
          &:disabled {
            color: ${theme.color['text/disabled']};
          }
        `;
      case 'secondary':
        return css`
          color: ${theme.color['text/weaker/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/ghost/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/ghost/press']};
          }
          &:disabled {
            color: ${theme.color['text/disabled']};
          }
        `;
      case 'accent':
        return css`
          color: ${theme.color['text/interactive/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/informative/weakest/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/informative/weakest/press']};
          }
          &:disabled {
            color: ${theme.color['text/disabled']};
          }
        `;
      case 'danger':
        return css`
          color: ${theme.color['text/danger/default']};
          &:hover {
            background-color: ${theme.color['actionSurface/danger/weak/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/danger/weak/press']};
          }
          &:disabled {
            color: ${theme.color['text/disabled']};
          }
        `;
      case 'inverse':
        return css`
          color: ${theme.color['text/global/default']};
          ${theme.mixins.focusRing('outside', { inverse: true })}
          &:hover {
            background-color: ${theme.color['actionSurface/ghostInverse/hover']};
          }
          &:active {
            background-color: ${theme.color['actionSurface/ghostInverse/press']};
          }
          &:disabled {
            color: ${theme.color['text/inverse/disabled']};
          }
        `;
    }
  }
  return css``;
};

const getSizeContentStyles = (
  size: ButtonBaseProps['size'],
  content: ButtonBaseProps['content'],
  theme: any,
) => {
  if (content === 'text') {
    switch (size) {
      case 'xl':
        return css`
          height: 48px;
          padding: 0 24px;
          ${theme.typography['label/medium/lg']};
        `;
      case 'lg':
        return css`
          height: 40px;
          padding: 0 20px;
          ${theme.typography['label/medium/md']};
        `;
      case 'md':
        return css`
          height: 32px;
          padding: 0 16px;
          ${theme.typography['label/medium/md']};
        `;
      case 'sm':
        return css`
          height: 28px;
          padding: 0 12px;
          ${theme.typography['label/medium/xs']};
        `;
    }
  } else if (content === 'icon') {
    switch (size) {
      case 'xl':
        return css`
          min-width: 48px;
          height: 48px;
          padding: 0 14px;
          font-size: 20px;
        `;
      case 'lg':
        return css`
          min-width: 40px;
          height: 40px;
          padding: 0 10px;
          font-size: 20px;
        `;
      case 'md':
        return css`
          min-width: 32px;
          height: 32px;
          padding: 0 8px;
          font-size: 16px;
        `;
      case 'sm':
        return css`
          min-width: 28px;
          height: 28px;
          padding: 0 6px;
          font-size: 16px;
        `;
    }
  }
  return css``;
};

const getTertiaryPaddingAdjustment = (
  appearance: ButtonBaseProps['appearance'],
  variant: ButtonBaseProps['variant'],
  content: ButtonBaseProps['content'],
  size: ButtonBaseProps['size'],
) => {
  if (appearance === 'solid' && variant === 'tertiary') {
    if (content === 'text') {
      switch (size) {
        case 'xl':
          return css`
            padding: 0 calc(24px - 1px);
          `;
        case 'lg':
          return css`
            padding: 0 calc(20px - 1px);
          `;
        case 'md':
          return css`
            padding: 0 calc(16px - 1px);
          `;
        case 'sm':
          return css`
            padding: 0 calc(12px - 1px);
          `;
      }
    } else if (content === 'icon') {
      switch (size) {
        case 'xl':
          return css`
            padding: 0 calc(14px - 1px);
          `;
        case 'lg':
          return css`
            padding: 0 calc(10px - 1px);
          `;
        case 'md':
          return css`
            padding: 0 calc(8px - 1px);
          `;
        case 'sm':
          return css`
            padding: 0 calc(6px - 1px);
          `;
      }
    }
  }
  return css``;
};

const getIconSizeStyles = (size: ButtonBaseProps['size']) => {
  switch (size) {
    case 'xl':
    case 'lg':
      return css`
        & .${muiButtonClasses.startIcon} {
          width: 24px;
          height: 24px;
          padding: 2px;
          font-size: 20px;
        }

        & .${muiButtonClasses.endIcon} {
          width: 20px;
          height: 20px;
          font-size: 20px;
        }
      `;
    case 'md':
      return css`
        & .${muiButtonClasses.startIcon} {
          width: 20px;
          height: 20px;
          padding: 2px;
          font-size: 16px;
        }

        & .${muiButtonClasses.endIcon} {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }
      `;
    case 'sm':
      return css`
        & .${muiButtonClasses.startIcon} {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }

        & .${muiButtonClasses.endIcon} {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }
      `;
  }
  return css``;
};

const getLoadingIndicatorPositionStyles = (
  loadingPosition: ButtonBaseProps['loadingPosition'],
  size: ButtonBaseProps['size'],
  content: ButtonBaseProps['content'],
) => {
  if (content !== 'text' || loadingPosition === 'center' || loadingPosition === undefined) {
    return css``;
  }

  const offsetMap = {
    xl: { start: 26, end: 24 },
    lg: { start: 22, end: 20 },
    md: { start: 18, end: 16 },
    sm: { start: 12, end: 12 },
  } as const;
  const offset = offsetMap[size ?? 'xl'][loadingPosition];

  if (loadingPosition === 'start') {
    return css`
      & .${muiButtonClasses.loadingIndicator} {
        left: ${offset}px;
      }
    `;
  }

  return css`
    & .${muiButtonClasses.loadingIndicator} {
      right: ${offset}px;
    }
  `;
};

const ButtonBaseRoot = styled(MuiButton, {
  target: buttonBaseClasses.root,
})<{ ownerState: ButtonBaseOwnerState }>(({ ownerState, theme }) => {
  const { appearance, variant, content, loadingPosition, size } = ownerState;

  return css`
    /* 기본 스타일 (theme.ts의 root에서 이전) */
    border-radius: 1px;
    text-transform: initial;
    ${theme.mixins.focusRing('outside')}

    & .${muiButtonClasses.icon} {
      margin: 0;

      & > *:nth-of-type(1) {
        font-size: inherit;
        width: 1em;
        height: 1em;
      }
    }

    transition: none;

    /* appearance & variant 스타일 */
    ${getAppearanceVariantStyles(appearance, variant, theme)}

    /* content & size 스타일 */
    ${getSizeContentStyles(size, content, theme)}

    /* tertiary variant의 border 보정 */
    ${getTertiaryPaddingAdjustment(appearance, variant, content, size)}

    /* startIcon & endIcon 스타일 */
    ${getIconSizeStyles(size)}

    /* loading indicator를 KCDS 아이콘 슬롯 중앙에 배치 */
    ${getLoadingIndicatorPositionStyles(loadingPosition, size, content)}

    &.${muiButtonClasses.loading}.${muiButtonClasses.loadingPositionCenter} {
      color: transparent;

      & > :not(.${muiButtonClasses.loadingWrapper}) {
        visibility: hidden;
      }
    }
  `;
});
